import React, {Component, ReactElement} from 'react'
import {MainContainer} from '../../../../styledComponents/ContentsArea'
import NatRoomNumberRepo from '../../roomNumber/repository/NatRoomNumberRepo'
import {NatGetRoomNumberList} from '../../roomNumber/repository/NatGetRoomNumberList'
import NatRidgeRepo from '../../ridge/repository/NatRidgeRepo'
import {NatGetRidge} from '../../ridge/repository/NatGetRidge'
import NatResidentUserSelectFormComponent from './NatResidentUserSelectFormComponent'
import NatResidentUserSelectReadComponent from './NatResidentUserSelectReadComponent'
import {PrimaryStyledButton} from '../../../../styledComponents/Button'
import {Nullable} from '../../../../app/TypeHelpers'
import {SelectedUser} from './NatResidentSelectedUser'
import EventEmitterExResident from '../../../../components/shared/pageBaseComponents/EventEmitterExResident'
import equal from 'fast-deep-equal'
import ResponsiveDialog from '../../../../components/shared/ResponsiveDialog'
import CloseIcon from '@mui/icons-material/Close'
import {StyledDiv} from '../../../../styledComponents/StyledTypeDefinitions'
import styled from 'styled-components'
import {Colors, FontSize, FontWeights} from '../../../../styledComponents/Constants'
import {DisplayItemDividerForPageTop} from '../../../../styledComponents/DisplayItem'
import {Box, DialogTitle as MuiDialogTitle, IconButton} from '@mui/material'
import {SessionStorageHelper} from '../../../../SessionStorageHelper'
import {NatAsyncCallFunction, reverseConvertToRoomNumberForResident} from '../../../common/util/Natutil'
import {StylesProvider} from '@mui/styles'
import moment from 'moment'
import {NavigateFunction} from 'react-router-dom'

interface Props {
    open: boolean,
    onCancel: () => void
    onUpdateSelectedUsers: (selectedUserList: SelectedUser[]) => void
    selectedUserList: SelectedUser[]
    roomNumberRepo: NatRoomNumberRepo
    ridgeRepo: NatRidgeRepo
    eventEmitter: EventEmitterExResident
    navigate: NavigateFunction
    maxSelectableKeyNumber?: number
    validPeriodCriteriaDate?: Nullable<moment.Moment>
    cardKeyNumberIsNotNullFlg?: boolean
}

interface States {
    roomNumberList: NatGetRoomNumberList
    ridgeList: NatGetRidge
    selectedUserList: SelectedUser[]
    isSearchCompleted: boolean
    isLoadCompleted: boolean
    checkStatus: { [key: string]: boolean };
    addButtonDisabled: boolean
}

const KEY_SELECTED_RIDGECD: string = 'selectedRidgeCd'
const KEY_SELECTED_ROOM_NUMBER: string = 'selectedRoomNumber'

export default class NatResidentUserSelectComponent extends Component<Props, States> {

    constructor(props: Props) {
        super(props)
        this.state = {
            roomNumberList: {
                details: null,
                informations: null,
                requestError: null,
                requestItemErrors: null,
            },
            ridgeList: {
                details: null,
                informations: null,
                requestError: null,
                requestItemErrors: null,
            },
            selectedUserList: this.props.selectedUserList,
            isSearchCompleted: false,
            checkStatus: {},
            isLoadCompleted: false,
            addButtonDisabled: false,
        }

        this.searchUser = this.searchUser.bind(this)
        this.cancelButtonClick = this.cancelButtonClick.bind(this)
        this.onUpdateSelectedUsers = this.onUpdateSelectedUsers.bind(this)
        this.addButtonClick = this.addButtonClick.bind(this)
    }

    async componentDidMount(): Promise<void> {
        await this.props.eventEmitter.showLoading()
        try {
            // 棟一覧取得
            const retRidgeList: Nullable<NatGetRidge> = await NatAsyncCallFunction(
                this.props.ridgeRepo.getRidgeList.bind(
                    this.props.ridgeRepo, undefined), this.props.navigate)

            // 初期チェック状態
            const checkStatus: { [key: string]: boolean } = {}
            this.props.selectedUserList.forEach((selectedUser) => {
                checkStatus[`${selectedUser.ridgeName}_${selectedUser.roomNumber}`] = true
            })

            if (retRidgeList && retRidgeList.details) {
                this.setState({
                    ridgeList: retRidgeList,
                    checkStatus: checkStatus,
                    isLoadCompleted: true,
                })
            }
        } finally {
            this.props.eventEmitter.closeLoading()
        }
    }

    componentDidUpdate(prevProps: Props, prevState: States): void {
        if (!equal(prevProps.selectedUserList, this.props.selectedUserList)) {
            // 初期チェック状態
            if (this.props.selectedUserList.length !== 0) {
                const checkStatus: { [key: string]: boolean } = {}
                this.props.selectedUserList.forEach((selectedUser) => {
                    checkStatus[`${selectedUser.ridgeName}_${selectedUser.roomNumber}`] = true
                })
                this.setState({
                    selectedUserList: JSON.parse(JSON.stringify(this.props.selectedUserList)),
                    checkStatus: checkStatus,
                    isSearchCompleted: false,
                })

                if (this.props.maxSelectableKeyNumber &&
                    this.state.selectedUserList.length > this.props.maxSelectableKeyNumber) {
                    this.setState({
                        addButtonDisabled: true,
                    })
                }
            } else {
                this.setState({
                    selectedUserList: JSON.parse(JSON.stringify(this.props.selectedUserList)),
                    isSearchCompleted: false,
                    checkStatus: {},
                    roomNumberList: {
                        details: null,
                        informations: null,
                        requestError: null,
                        requestItemErrors: null,
                    },
                })
            }
        }
    }

    // 検索
    async searchUser(ridgeCd: string, roomNumber: string): Promise<void> {
        await this.props.eventEmitter.showLoading()
        try {
            let validPeriodCriteriaDate: Nullable<moment.Moment> = null
            if (this.props.validPeriodCriteriaDate !== undefined) {
                validPeriodCriteriaDate = this.props.validPeriodCriteriaDate
            }
            // 部屋番号一覧取得
            const retRoommNumberList: Nullable<NatGetRoomNumberList> =
                await NatAsyncCallFunction(
                    this.props.roomNumberRepo.getRoomNumberList.bind(
                        this.props.roomNumberRepo,
                        ridgeCd,
                        reverseConvertToRoomNumberForResident(roomNumber),
                        validPeriodCriteriaDate,
                        this.props.cardKeyNumberIsNotNullFlg ?? false,
                    ), this.props.navigate)
            if (retRoommNumberList && retRoommNumberList.details) {
                this.setState({
                    roomNumberList: this.excludingMyselfRecode(retRoommNumberList),
                    isSearchCompleted: true,
                })
            }
        } finally {
            this.props.eventEmitter.closeLoading()
        }
    }

    // 選択中の入室番号を更新
    onUpdateSelectedUsers(selectedUserList: SelectedUser[], checkStatus: { [key: string]: boolean }): void {
        this.setState({selectedUserList: selectedUserList, checkStatus: checkStatus})
        if (this.props.maxSelectableKeyNumber && selectedUserList.length > this.props.maxSelectableKeyNumber) {
            this.setState({addButtonDisabled: true})
        } else {
            this.setState({addButtonDisabled: false})
        }
    }

    // 同時入室番号を追加
    addButtonClick(): void {
        this.props.onUpdateSelectedUsers(this.state.selectedUserList)
    }

    // キャンセル
    cancelButtonClick(): void {
        this.props.onCancel()
        const checkStatus: { [key: string]: boolean } = {}
        this.props.selectedUserList.forEach((selectedUser) => {
            checkStatus[`${selectedUser.ridgeName}_${selectedUser.roomNumber}`] = true
        })
        let addButtonDisabled: boolean = false

        if (this.props.maxSelectableKeyNumber &&
            this.props.selectedUserList.length > this.props.maxSelectableKeyNumber) {
            addButtonDisabled = true
        }

        this.setState({
            selectedUserList: JSON.parse(JSON.stringify(this.props.selectedUserList)),
            checkStatus: checkStatus,
            isSearchCompleted: false,
            addButtonDisabled: addButtonDisabled
        })
    }

    excludingMyselfRecode(roomNumberList: NatGetRoomNumberList): NatGetRoomNumberList {
        const selectedRidgeCd: Nullable<string> =
            SessionStorageHelper.getStringItemOrNull(KEY_SELECTED_RIDGECD)
        const selectedRoomNumber: Nullable<string> =
            SessionStorageHelper.getStringItemOrNull(KEY_SELECTED_ROOM_NUMBER)

        if (selectedRidgeCd === null || selectedRoomNumber === null) {
            return roomNumberList
        }

        if (roomNumberList && roomNumberList.details) {
            roomNumberList.details.roomNumberInfo = roomNumberList.details.roomNumberInfo.filter(
                (roomNumberInfo) => (
                    roomNumberInfo.ridgeCd !== selectedRidgeCd || roomNumberInfo.roomNumber !== selectedRoomNumber))
        }

        return roomNumberList

    }

    render(): Nullable<ReactElement> {
        if (this.state.isLoadCompleted) {
            return (
                <>
                    <StylesProvider injectFirst>
                        <ResponsiveDialog
                            id={'pp-dialog'}
                            open={this.props.open}
                            transitionDuration={(process.env.NODE_ENV === 'test') ? 0 : undefined}
                            fullWidth
                        >
                            <MainContainer>
                                <StyledMuiDialogTitle>
                                    <SelectUserTitle>同時入室番号</SelectUserTitle>
                                    <StyledIconButton aria-label="close" onClick={this.cancelButtonClick}>
                                        <CloseIcon fontSize={'large'}/>
                                    </StyledIconButton>
                                </StyledMuiDialogTitle>
                                <DisplayItemDividerForPageTop/>
                                <NatResidentUserSelectFormComponent
                                    searchUser={this.searchUser}
                                    ridgeList={this.state.ridgeList}
                                />
                                <NatResidentUserSelectReadComponent
                                    selectedUserList={this.state.selectedUserList}
                                    checkStatus={this.state.checkStatus}
                                    roomNumberList={this.state.roomNumberList}
                                    onUpdateSelectedUsers={this.onUpdateSelectedUsers}
                                    isSearchCompleted={this.state.isSearchCompleted}
                                />
                                {
                                    (this.state.isSearchCompleted &&
                                        this.state.roomNumberList.details !== null &&
                                        this.state.roomNumberList.details.roomNumberInfo.length > 0) &&
                                    <Box margin={'33px 0 15px 0'}>
                                        <PrimaryStyledButton
                                            onClick={this.addButtonClick}
                                            disabled={this.state.addButtonDisabled}>
                                            設定する
                                        </PrimaryStyledButton>
                                    </Box>
                                }
                            </MainContainer>
                        </ResponsiveDialog>
                    </StylesProvider>
                </>
            )
        } else {
            return null
        }
    }
}

const StyledMuiDialogTitle = styled(MuiDialogTitle)`
  &.MuiDialogTitle-root {
    padding: 0 16px 16px 0;
    font-size: ${FontSize.Accent};
    color: ${Colors.AccentDark};
  }
`

const SelectUserTitle: StyledDiv = styled.div`
  font-size: ${FontSize.Accent};
  color: ${Colors.AccentDark};
  font-weight: ${FontWeights.Bold};
`

const StyledIconButton = styled(IconButton)`
  top: 8px;
  right: 8px;
  position: absolute;
`
